import React, { useState, useEffect } from "react";

import { useParams } from "react-router-dom";

import { useTheme } from "@mui/material/styles";

import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";

import {
  Authorization,
  authorized,
  formatLocalizedMoney,
  logDebug,
} from "packable-components";
import { PackableAppContext } from "packable-components";
import { mergeProperties } from "packable-components";
import { SnackbarComponentVariant } from "packable-components";

import {
  IClientEntity,
  IPackableInventory,
  IUserAppState,
  ItemSearch,
  PackableInventory,
  ScanItem,
  SystemInfo,
  UserActivityList,
  ViewItemDetails,
} from "packable-common";
import { Grid, IconButton, Tooltip, Typography } from "@mui/material";
import AddItem from "./AddItem";
import OrderStatusSummaryComponent from "../orders/OrderStatusSummaryComponent";

const useStyles = (theme: any) => ({
  PackableHomePage: {
    height: "100hv",
  },
  PackableHomeContainer: {
    p: 4,
    marginLeft: "10%",
    width: "100%",
    height: "100%",
    display: "flex",
    flexDirection: "column",
    [theme.breakpoints.down("sm")]: {
      marginLeft: "0%",
    },
  },
  PackableHomeInventoryName: {
    muiVariant: "h4",
  },
  PackableHomeInventorySummaryContainer: {
    display: "flex",
    flexWrap: "wrap",
    flexDirection: "row",
  },
  PackableHomeInventorySummaryItem: {
    mr: 1,
    muiVariant: "body1",
  },

  PackableItemTagsContainer: {
    width: "400px",
    display: "flex",
    flexDirection: "row",
    flexWrap: "wrap",
  },
  PackableSalectableChip: {
    muiSize: "small",
  },
  PackableListItemsContainer: {},
  PackableListItemsLabel: {
    muiVariant: "h6",
  },
  PackableListItemsImageContainer: {
    width: "100%",
    display: "flex",
    flexDirection: "row",
    flexWrap: "wrap",
    alignContent: "center",
    justifyContent: "center",
    alignItems: "center",
    [theme.breakpoints.up("sm")]: {
      justifyContent: "flex-start",
    },
  },
  PackableListItemsImage: {
    fontSize: "80px",
    color: theme.palette.primary.main,
  },
});

const inventoryURL = "/inventory";

export interface HomePageProps {
  displayMessage: (variant: SnackbarComponentVariant, message: string) => void;
  setDisplaySpinner: (flag: boolean) => void;
  userAppState: IUserAppState;
  clientEntity: IClientEntity;
  notificationCount: number;
  onUserAppStateUpdate: (userAppState: IUserAppState) => void;
  windowWidth: number;
  windowHeight: number;
  [x: string | number | symbol]: unknown;
  styles?: any;
}

export const HomePage: React.FC<HomePageProps> = (props): React.JSX.Element => {
  const {
    userAppState,
    onUserAppStateUpdate,
    clientEntity,
    displayMessage,
    setDisplaySpinner,
    windowHeight,
    windowWidth,
    styles,
  } = props;
  const { httpService } = React.useContext(PackableAppContext);
  const [currentInventory, setCurrentInventory] =
    useState<IPackableInventory | null>(new PackableInventory());
  const [isItemDetailsShowing, setIsItemDetailsShowing] =
    useState<boolean>(false);

  const theme = useTheme();

  logDebug("HomePage PROPS");
  logDebug(props);

  const params = useParams();

  logDebug("HomePage params");
  logDebug(params);

  const defaultStyles = useStyles(theme);
  const effectiveStyles =
    styles === undefined
      ? defaultStyles
      : mergeProperties(defaultStyles, styles);

  logDebug("HomePage Effective Styles:");
  logDebug(effectiveStyles);

  const onCurrentInventoryLoadSuccess = (data: any): void => {
    setCurrentInventory(data.data);
  };
  const onCurrentInventoryLoadError = (message: string, data: any): void => {
    displayMessage(SnackbarComponentVariant.error, message);
  };

  const loadCurrentInventory = (currentInventoryId: string): void => {
    httpService.get(
      inventoryURL + "/" + userAppState.currentInventoryId,
      onCurrentInventoryLoadSuccess,
      onCurrentInventoryLoadError,
      setDisplaySpinner
    );
  };

  const isAdminUser = (): boolean => {
    return authorized("system", Authorization.MANAGE);
  };

  const handleCloseItemDetails = (): void => {
    setIsItemDetailsShowing(false);
    window.location.href = "/";
  };

  useEffect(() => {
    if (params.inventoryId !== undefined && params.inventoryId !== null) {
      if (params.inventoryId !== userAppState.currentInventoryId) {
        userAppState.currentInventoryId = params.inventoryId;
        onUserAppStateUpdate(userAppState);
      }
    }
    loadCurrentInventory(userAppState.currentInventoryId);
    if (params.itemId !== undefined && params.itemId !== null) {
      setIsItemDetailsShowing(true);
    }
  }, []);

  return (
    <>
      {isAdminUser() ? (
        <>
          <SystemInfo
            displayMessage={displayMessage}
            setDisplaySpinner={setDisplaySpinner}
            windowWidth={windowWidth}
            windowHeight={windowHeight}
            styles={effectiveStyles}
          />
          <OrderStatusSummaryComponent
            displayMessage={displayMessage}
            setDisplaySpinner={setDisplaySpinner}
            windowWidth={windowWidth}
            windowHeight={windowHeight}
            styles={effectiveStyles}
          />
          <UserActivityList {...props} />
        </>
      ) : (
        <>
          <div
            className="PackableHomePage"
            style={effectiveStyles.PackableHomePage}
          >
            <Grid
              className="PackableHomeContainer"
              sx={effectiveStyles.PackableHomeContainer}
            >
              {currentInventory !== undefined &&
                currentInventory !== null &&
                currentInventory.name !== undefined &&
                currentInventory.name !== null &&
                currentInventory.name.length > 0 && (
                  <Typography
                    className="PackableHomeInventoryName"
                    variant={
                      effectiveStyles.PackableHomeInventoryName.muiVariant
                    }
                  >
                    {`${currentInventory?.name} (${currentInventory?.prefix})`}
                  </Typography>
                )}
              <Grid
                className="PackableHomeInventorySummaryContainer"
                sx={effectiveStyles.PackableHomeInventorySummaryContainer}
              >
                <Typography
                  className="PackableHomeInventorySummaryItem"
                  sx={effectiveStyles.PackableHomeInventorySummaryItem}
                  variant={
                    effectiveStyles.PackableHomeInventorySummaryItem.muiVariant
                  }
                >
                  {`Total Items: ${currentInventory?.itemCount}`}
                </Typography>
                <Typography
                  className="PackableHomeInventorySummaryItem"
                  sx={effectiveStyles.PackableHomeInventorySummaryItem}
                  variant={
                    effectiveStyles.PackableHomeInventorySummaryItem.muiVariant
                  }
                >
                  {`Total Value: ${formatLocalizedMoney(
                    currentInventory?.totalValue === undefined
                      ? 0
                      : currentInventory.totalValue,
                    0,
                    userAppState.units?.currency,
                    Intl.DateTimeFormat().resolvedOptions().locale
                  )}`}
                </Typography>
              </Grid>
              <Grid
                className="PackableHomeInventorySummaryContainer"
                sx={effectiveStyles.PackableHomeInventorySummaryContainer}
              >
                <Typography
                  className="PackableHomeInventorySummaryItem"
                  sx={effectiveStyles.PackableHomeInventorySummaryItem}
                  variant={
                    effectiveStyles.PackableHomeInventorySummaryItem.muiVariant
                  }
                >
                  {`Boxes: ${currentInventory?.totalBoxes}`}
                </Typography>
                <Typography
                  className="PackableHomeInventorySummaryItem"
                  sx={effectiveStyles.PackableHomeInventorySummaryItem}
                  variant={
                    effectiveStyles.PackableHomeInventorySummaryItem.muiVariant
                  }
                >
                  {`Individual Items: ${currentInventory?.totalItems}`}
                </Typography>
              </Grid>
              <Grid sx={{ pt: 1 }}>
                <ItemSearch
                  userAppState={userAppState}
                  clientEntity={clientEntity}
                  styles={effectiveStyles}
                />
              </Grid>
              <Grid sx={{ pt: 1 }}>
                <Grid
                  className="PackableListItemsContainer"
                  sx={effectiveStyles.PackableListItemsContainer}
                >
                  <Typography
                    sx={effectiveStyles.PackableListItemsLabel}
                    variant={effectiveStyles.PackableListItemsLabel.muiVariant}
                  >
                    List Items
                  </Typography>
                  <Grid
                    className="PackableListItemsImageContainer"
                    sx={effectiveStyles.PackableListItemsImageContainer}
                  >
                    <Tooltip title="Items List">
                      <IconButton href="/items">
                        <FormatListBulletedIcon
                          className="PackableListItemsImage"
                          style={effectiveStyles.PackableListItemsImage}
                        />
                      </IconButton>
                    </Tooltip>
                  </Grid>
                </Grid>
              </Grid>
              <Grid sx={{ pt: 1 }}>
                <ScanItem
                  windowHeight={windowHeight}
                  windowWidth={windowWidth}
                  styles={effectiveStyles}
                />
              </Grid>
              <Grid sx={{ pt: 1 }}>
                <AddItem
                  displayMessage={displayMessage}
                  setDisplaySpinner={setDisplaySpinner}
                  userAppState={userAppState}
                  clientEntity={clientEntity}
                  windowHeight={windowHeight}
                  windowWidth={windowWidth}
                  styles={effectiveStyles}
                />
              </Grid>
            </Grid>
            {isItemDetailsShowing && clientEntity !== undefined && (
              <ViewItemDetails
                displayMessage={displayMessage}
                setDisplaySpinner={setDisplaySpinner}
                isShowing={isItemDetailsShowing}
                onClose={handleCloseItemDetails}
                userAppState={userAppState}
                clientEntity={clientEntity}
                itemId={params.itemId !== undefined ? params.itemId : null}
                windowHeight={windowHeight}
                windowWidth={windowWidth}
                styles={effectiveStyles}
              />
            )}
          </div>
        </>
      )}
    </>
  );
};

export default HomePage;
